import { useBookingLocale } from '../../hooks/useBookingLocale';
import React, { useCallback, useEffect, useState } from 'react';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Modal } from '../modal';
import { Button } from '../button';
import { CardSetupForm } from '../CardSetupForm';
import { useStripePromise } from '../../hooks/useStripePromise';
import {
  useBookingPaymentOptionsQuery,
  useConfirmOutstandingInvoicePaymentMutation,
  useCreateSetupIntentForBookingMutation,
  usePayOutstandingInvoiceMutation,
} from '../../services/stripeService/__generated__/queries.generated';
import { ExtensionCardPicker } from '../ExtendTripPaymentModal/ExtensionCardPicker';
import {
  initialCardChoice,
  NEW_CARD_CHOICE,
  nextCardAfterDecline,
} from '../ExtendTripPaymentModal/extensionPayment';
import { formatChargeAmount, nextInvoicePaymentStep } from './outstandingCharges';

const Body = styled.div`
  ${tw`w-full`}
`;

const Title = styled.h2`
  ${tw`text-lg font-semibold mb-1`}
  color: var(--color-text-primary);
`;

const Lead = styled.p`
  ${tw`text-sm mb-2`}
  color: var(--color-text-secondary);
`;

const ErrorNotice = styled.p`
  ${tw`text-sm rounded-md px-3 py-2 my-2`}
  color: #b91c1c;
  background: #fef2f2;
`;

export interface OutstandingInvoiceRow {
  id: string;
  amount: number;
  label: string;
}

interface Props {
  bookingId: string;
  invoice: OutstandingInvoiceRow | null;
  onClose: () => void;
  onPaid: () => void;
}

const PayInvoiceForm: React.FC<Omit<Props, 'invoice'> & { invoice: OutstandingInvoiceRow }> = ({
  bookingId,
  invoice,
  onClose,
  onPaid,
}) => {
  // Spec 028: the trip's currency.
  const { currency } = useBookingLocale(bookingId, 'guest');
  const stripe = useStripe();
  const { data, refetch } = useBookingPaymentOptionsQuery({
    variables: { bookingId },
    fetchPolicy: 'network-only',
  });
  const options = data?.bookingPaymentOptions;
  const cards = options?.cards ?? [];
  const [choice, setChoice] = useState<string | null>(null);
  const [formKey, setFormKey] = useState(0);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payInvoice] = usePayOutstandingInvoiceMutation();
  const [confirmPayment] = useConfirmOutstandingInvoicePaymentMutation();
  const [createSetupIntent] = useCreateSetupIntentForBookingMutation();

  useEffect(() => {
    if (options && choice === null) {
      setChoice(initialCardChoice(options.cards, options.preferredPaymentMethodId));
    }
  }, [options, choice]);

  const loadSetup = useCallback(async () => {
    const { data: answer } = await createSetupIntent({
      // The list above already offers the trip's and saved cards: open on an empty card form.
      variables: { bookingId, showSavedCards: false },
    });
    const setup = answer?.createSetupIntentForBooking;
    if (!setup?.clientSecret) throw new Error('The card form could not be opened.');
    return {
      clientSecret: setup.clientSecret,
      customerSessionClientSecret: setup.customerSessionClientSecret,
    };
  }, [createSetupIntent, bookingId]);

  const afterFailure = (message: string, paymentMethodId: string | null) => {
    setError(message);
    void refetch().catch(() => undefined);
    if (choice === NEW_CARD_CHOICE) setFormKey(key => key + 1);
    else if (paymentMethodId) setChoice(nextCardAfterDecline(cards, paymentMethodId));
  };

  const pay = async (paymentMethodId: string | null) => {
    if (!stripe) return;
    setPaying(true);
    setError(null);
    try {
      const { data: answer } = await payInvoice({
        variables: {
          input: {
            invoiceId: invoice.id,
            paymentMethodId: paymentMethodId || undefined,
            platform: 'web',
          },
        },
      });
      const result = answer?.payOutstandingInvoice;
      if (!result) throw new Error('No answer from the payment service.');
      const step = nextInvoicePaymentStep(result);
      if (step.kind === 'failed') {
        afterFailure(step.message, paymentMethodId);
        return;
      }
      if (step.kind === 'authenticate') {
        const challenge = await stripe.confirmCardPayment(step.clientSecret);
        if (challenge.error || challenge.paymentIntent?.status !== 'succeeded') {
          afterFailure(
            challenge.error?.message || 'The bank did not approve this payment.',
            paymentMethodId,
          );
          return;
        }
        await confirmPayment({
          variables: { invoiceId: invoice.id, paymentIntentId: step.paymentIntentId },
        });
      }
      toast.success(`Paid ${formatChargeAmount(invoice.amount, currency)}. Thank you.`);
      onPaid();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment failed. Please try again.');
    } finally {
      setPaying(false);
    }
  };

  return (
    <Body>
      <Title>Pay {invoice.label.toLowerCase()}</Title>
      <Lead>Amount due: {formatChargeAmount(invoice.amount, currency)}</Lead>
      {error ? <ErrorNotice role="alert">{error}</ErrorNotice> : null}
      {options ? (
        <ExtensionCardPicker
          cards={cards}
          value={choice}
          onChange={next => {
            setChoice(next);
            setError(null);
          }}
          disabled={paying}
        />
      ) : null}
      {choice === NEW_CARD_CHOICE ? (
        <CardSetupForm
          key={formKey}
          loadSetup={loadSetup}
          onCardReady={({ paymentMethodId }) => pay(paymentMethodId)}
          submitLabel={`Pay ${formatChargeAmount(invoice.amount, currency)}`}
          notice="Use a card in your own name. Your bank may ask you to confirm this payment."
          busy={paying}
        />
      ) : (
        <Button
          text={`Pay ${formatChargeAmount(invoice.amount, currency)}`}
          onClick={() => pay(choice)}
          isLoading={paying}
          isDisabled={paying || !options}
        />
      )}
    </Body>
  );
};

/**
 * Spec 013 P5 (FR-023): the guest pays an unpaid post-trip charge with a card of this
 * trip, a card saved on this site, or a new card. The server charges; a bank check is
 * completed here and then verified by the server.
 */
export const PayOutstandingInvoiceModal: React.FC<Props> = props => {
  const { stripePromise, publishableKey, ready } = useStripePromise();
  return (
    <Modal
      isOpen={Boolean(props.invoice)}
      onClose={props.onClose}
      modalWidth="min(480px, calc(100vw - 2rem))"
      modalHeight="auto"
      title="Pay a trip charge"
    >
      {ready && props.invoice ? (
        <Elements stripe={stripePromise} key={publishableKey}>
          <PayInvoiceForm {...props} invoice={props.invoice} />
        </Elements>
      ) : null}
    </Modal>
  );
};
